/**
 * Armenian catalog (Armenian script) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Eastern Armenian at a mathematician's register. **Warrant →
 * «Հիմնավորում»** (justification — flagged). This batch adds the Armenian writing system to the
 * infrastructure: `scriptOf('hy')` → `armenian` (new), paired with a Noto-led Armenian font
 * stack (`font-stacks.ts`). Armenian keeps the noun SINGULAR after a numeral, so the one/other
 * forms of «սահմանափակում» coincide — both correct. W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other (`one` covers 0–1), so
 * `frameActive` declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const HY_CATALOG = {
    locale: 'hy',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Հետազոտություն',
        [P2.sectionClaims]: 'Պնդումներ',
        [P2.sectionIntegrity]: 'Ամբողջականություն',
        [P2.axisWarrantLabel]: 'Հիմնավորում',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Էմպիրիկ',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Սահմանում} interp {Մեկնաբանություն} testable {Ստուգելի} tested {Ստուգված} refuted {Հերքված} program {Հետազոտական ծրագիր} philos {Փիլիսոփայական} other {Անհայտ}}',
        [P2.axisModalLabel]: 'Մոդալ',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ուժի մակարդակ {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Ուսումնասիրելի',
        [P2.stateRefuted]: 'Հերքված',
        [P2.stateOutside]: 'Շրջանակից դուրս',
        [P2.verdictConflict]: 'Հակասում է {constraints}-ին',
        [P2.verdictOutside]: 'Պահանջում է {missing}',
        [P2.lossRegisterPlain]: 'Պարզեցված է սովորական լեզվի — ճշգրտության մի մասը բաց է թողնված։',
        [P2.lossRegisterKid]: 'Պարզեցված է փոքր ընթերցողների համար — շատ մանրամասներ բաց են թողնված։',
        [P2.lossProfileDetail]: 'Ամբողջական Հիմնավորում / Էմպիրիկ / Մոդալ պրոֆիլն ամփոփված է այստեղ։',
        [P2.lossConflictDetail]: 'Հակասության մանրամասներն այս տեսքում կրճատված են։',
        [P2.laneVerified]: 'Հաստատված',
        [P2.laneUnverified]: 'Չհաստատված',
        [P2.laneConflict]: 'Հակասությունն ապացուցված է',
        [P2.constraintLogic]: 'Տրամաբանություն',
        [P2.constraintLightBends]: 'Լույսը թեքվում է գրավիտացիոն դաշտում',
        [P2.constraintParallelPostulate]: 'Զուգահեռների պոստուլատ',
        [P2.claimScalarGravity]: 'Գրավիտացիայի սկալյար տեսություն',
        [P2.claimNonEuclidean]: 'Ոչ էվկլիդեսյան երկրաչափություն',
        [P2.claimSubstrateSr]: 'Հատուկ հարաբերականության ենթաշերտային մեկնաբանություն',
        [P2.edgeExtObservation]: 'Արտաքին դիտարկում',
        [P2.edgeResourceSample]: 'Ընդհանուր հաշվարկային ռեսուրս',
        [P2.edgeHeuristicSample]: 'Էվրիստիկ ուղղորդում',
        [P2.edgeSharedSample]: 'Ընդհանուր հիմք',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Սահմանափակումներ',
        [P2.sectionEdges]: 'Կողեր',
        [P2.sectionFrame]: 'Շրջանակ',
        [P2.sectionExposure]: 'Ենթարկվածություն',
        [P2.statusAdoptedFormal]: 'Ընդունված (ֆորմալ)',
        [P2.statusAdoptedInterpretive]: 'Ընդունված (մեկնաբանական)',
        [P2.statusSuspended]: 'Կասեցված',
        [P2.edgeEffectConstraint]: 'Կազմում է սահմանափակում (կարող է հերքել)',
        [P2.edgeEffectResource]: 'Ռեսուրս — չի ազդում վճիռների վրա',
        [P2.edgeEffectHeuristic]: 'Էվրիստիկա — չի ազդում վճիռների վրա',
        [P2.edgeEffectShared]: 'Ընդհանուր հիմք — չի ազդում վճիռների վրա',
        [P2.frameActive]: '{count, plural, =0 {Ակտիվ սահմանափակումներ չկան} one {# ակտիվ սահմանափակում} other {# ակտիվ սահմանափակում}}',
        [P2.exposureNone]: 'Մեկնաբանական կախվածություններ չկան',
        [P2.exposureDep]: 'Կախված է {id}-ից',
        [P2.laneTrackMachine]: 'Մեքենա',
        [P2.laneTrackExpert]: 'Փորձագետ',
        [P2.laneTrackCommunity]: 'Համայնք',
        [P2.expertAudited]: 'Աուդիտ անցած',
        [P2.expertInProgress]: 'Ընթացքի մեջ',
        [P2.expertNotReviewed]: 'Չվերանայված',
        [P2.communityFlags]: 'Կան նշումներ',
        [P2.communityNone]: 'Նշումներ չկան',
        [P2.controlChallenge]: 'Վիճարկել',
        [P2.controlActivate]: 'Ակտիվացնել',
        [P2.controlMakeInterpretive]: 'Դարձնել մեկնաբանական',
        [P2.controlImportEdge]: 'Ներմուծել կող',
        [P2.controlPromote]: 'Բարձրացնել',
        [P2.controlUndo]: 'Հետարկել',
        [P2.controlRedo]: 'Կրկնել',
        [P2.controlRegister]: 'Գրանցել',
        [P2.promoteRejected]: 'Բարձրացումը մերժվեց',
        [P2.promoteAccepted]: 'Բարձրացումն ընդունվեց',
        [P2.promoteReasonRefutationTerminal]: 'Ապացույցը չի կարող բուժել հերքումը։',
        [P2.promoteReasonKeystone]: 'Առանցքային բարձրացումը պահանջում է էմպիրիկ աջակցություն։',
        [P2.promoteReasonEmpiricalNotFixed]: 'Էմպիրիկ տեսակը ֆիքսված չէ։',
        [P2.promoteReasonModalNotFixed]: 'Մոդալ աստիճանը ֆիքսված չէ։',
        [P2.promoteReasonWarrantNotImproved]: 'Հիմնավորման աստիճանը չի բարելավվել։',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=hy.js.map